import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '@modules/auth/hooks';
import { PATIENT_PROFILE_QUERY_KEY } from '../constants';
import { fetchPatientProfile } from '@modules/genetic-counseling/services';
import { PatientProfileResponse, UseQueryPatientProfileParams } from '../types';

const useQueryPatientProfile = <TError = unknown>(
  { onError, onSettled, onSuccess, id }: UseQueryPatientProfileParams,
  options?: Omit<
    UseQueryOptions<
      PatientProfileResponse | undefined,
      TError,
      PatientProfileResponse | undefined,
      Array<string | undefined>
    >,
    'initialData' | 'queryFn' | 'queryKey'
  >,
): UseQueryResult<PatientProfileResponse | undefined> => {
  const { isAuthenticated } = useAuth();

  const enabledOption = options?.enabled !== undefined ? !!options?.enabled : true;

  const onSuccessFn = (data: PatientProfileResponse | undefined): void => {
    onSuccess?.(data);
  };

  const onErrorFn = (err: unknown): void => {
    onError?.(err);
  };

  const onSettledFn = (_data: PatientProfileResponse | undefined, _error: unknown): void => {
    onSettled?.();
  };

  return useQuery([PATIENT_PROFILE_QUERY_KEY, id], async () => await fetchPatientProfile({ id }), {
    ...options,
    retry: 2,
    onSuccess: onSuccessFn,
    onError: onErrorFn,
    onSettled: onSettledFn,
    enabled: isAuthenticated === true && enabledOption,
  });
};

export default useQueryPatientProfile;
